import { templateBodyOf } from './promptFill';
import {
  PromptChipInput,
  PromptTemplateVariable,
} from './SlashExtension.types';

/** What the menu's preview pane shows for a row (round two, B). */
export type PromptMenuPreview = {
  title: string;
  /** The template's text with its `{name}` slots. */
  body: string;
  variables: PromptTemplateVariable[];
};

const VAR = /\{([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)\}/g;

/** The preview for a row's chip: a template, or today's plain string. */
export const previewOf = (
  chip: PromptChipInput | undefined
): PromptMenuPreview | undefined => {
  if (chip === undefined) return undefined;
  if (typeof chip === 'string') {
    return { title: chip, body: chip, variables: [] };
  }
  const body = templateBodyOf(chip);
  // Every slot in the body, described where the template says.
  const described = new Map(
    (chip.variables ?? []).map(variable => [variable.name, variable])
  );
  const names = [...new Set([...body.matchAll(VAR)].map(match => match[1]))];
  return {
    title: chip.title,
    body,
    variables: names.map(name => described.get(name) ?? { name }),
  };
};

/**
 * Inside a group, a title that starts with the group's drill-in word splits
 * into a muted prefix and the rest, so the part that tells rows apart reads
 * first. Elsewhere the title stays whole.
 */
export const splitPrefix = (
  title: string,
  highlight: string | undefined
): { label: string; prefix?: string } => {
  const word = highlight?.trim();
  if (!word || title.length <= word.length + 1) return { label: title };
  if (title.slice(0, word.length).toLowerCase() !== word.toLowerCase()) {
    return { label: title };
  }
  const rest = title.slice(word.length);
  // Only a whole word: "Summarise the…", never "Summarised…".
  if (!/^[\s,:;!?–-]/.test(rest)) return { label: title };
  const prefix = title.slice(0, title.length - rest.trimStart().length);
  return { prefix, label: rest.trimStart() };
};
